import { useState } from "react";
import { Trophy, Shield, Users, Calendar, Target, Award, Play, Download, X } from "lucide-react";
import { Link } from "react-router-dom";
import { usePublicData } from "../../hooks/usePublicData";
import { Button } from "../../components/ui/button";

export default function PublicDashboard() {
  const { data, loading, token } = usePublicData();
  const [showVideo, setShowVideo] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { teams, players, matches, standings, player_stats } = data;
  const completedMatches = matches.filter((m) => m.status === "completed");
  const upcomingMatches = matches.filter((m) => m.status === "scheduled");
  const totalGoals = player_stats.reduce((sum, p) => sum + p.goals, 0);

  const topScorers = [...player_stats]
    .filter((p) => p.goals > 0)
    .sort((a, b) => b.goals - a.goals)
    .slice(0, 5);

  const mvpLeaders = [...player_stats]
    .filter((p) => p.mvp_awards > 0)
    .sort((a, b) => b.mvp_awards - a.mvp_awards)
    .slice(0, 5);

  // Get recent results
  const recentMatches = [...matches]
    .filter((m) => m.status === "completed")
    .sort((a, b) => new Date(b.match_date) - new Date(a.match_date))
    .slice(0, 3);

  // Get next matches
  const nextMatches = [...matches]
    .filter((m) => m.status === "scheduled")
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
    .slice(0, 3);

  const getTeam = (id) => teams.find((t) => t.id === id);
  const videoUrl = "/standings_race.webm";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Tournament overview and latest updates
        </p>
      </div>

      {/* Season Timeline Video Banner */}
      <div className="bg-gradient-to-r from-primary/20 via-amber-500/20 to-primary/20 border border-primary/30 rounded-xl p-4 md:p-6" data-testid="dashboard-video-banner">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-lg md:text-xl font-bold uppercase flex items-center justify-center md:justify-start gap-2">
              <Play className="w-5 h-5 text-primary" />
              Season Timeline
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Watch how the standings evolved from Day 1 to Championship Day!
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowVideo(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="dashboard-watch-timeline-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              Watch Video
            </Button>
            <Button variant="outline" asChild data-testid="dashboard-download-timeline-btn">
              <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                <Download className="w-4 h-4 mr-2" />
                Download
              </a>
            </Button>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowVideo(false)}>
          <div className="relative w-full max-w-4xl bg-card rounded-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold uppercase">Season Standings Timeline</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowVideo(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <video
                src={videoUrl}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: "70vh" }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="p-4 border-t border-border flex justify-end">
              <Button variant="outline" asChild>
                <a href={videoUrl} download="goodwillers_standings_timeline.webm">
                  <Download className="w-4 h-4 mr-2" />
                  Download Video
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-primary" />
            <span className="text-xs text-muted-foreground uppercase">Teams</span>
          </div>
          <div className="font-heading text-3xl font-black">{teams.length}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-accent" />
            <span className="text-xs text-muted-foreground uppercase">Players</span>
          </div>
          <div className="font-heading text-3xl font-black">{players.length}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            <span className="text-xs text-muted-foreground uppercase">Matches</span>
          </div>
          <div className="font-heading text-3xl font-black">
            {completedMatches.length}/{matches.length}
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 md:p-6">
          <div className="flex items-center gap-3 mb-2">
            <Target className="w-5 h-5 text-green-500" />
            <span className="text-xs text-muted-foreground uppercase">Goals</span>
          </div>
          <div className="font-heading text-3xl font-black">{totalGoals}</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Results & Upcoming */}
        <div className="lg:col-span-2 space-y-6">
          {/* Next Matches */}
          {nextMatches.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold uppercase">
                  Upcoming Matches
                </h3>
                <Link
                  to={`/public/${token}/matches`}
                  className="text-xs text-primary hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-3">
                {nextMatches.map((match) => {
                  const home = getTeam(match.home_team_id);
                  const away = getTeam(match.away_team_id);
                  return (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 bg-background rounded-lg"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {home?.logo ? (
                          <img src={home.logo} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                            <Shield className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="text-sm font-medium truncate">{home?.name}</span>
                      </div>
                      <div className="px-3 text-xs text-muted-foreground">
                        {match.match_date} • {match.match_time}
                      </div>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <span className="text-sm font-medium truncate text-right">{away?.name}</span>
                        {away?.logo ? (
                          <img src={away.logo} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                            <Shield className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recent Results */}
          {recentMatches.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-lg font-bold uppercase">
                  Recent Results
                </h3>
                <Link
                  to={`/public/${token}/matches`}
                  className="text-xs text-primary hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-3">
                {recentMatches.map((match) => {
                  const home = getTeam(match.home_team_id);
                  const away = getTeam(match.away_team_id);
                  return (
                    <div
                      key={match.id}
                      className="flex items-center justify-between p-3 bg-background rounded-lg"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        {home?.logo ? (
                          <img src={home.logo} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                            <Shield className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <span className="text-sm font-medium truncate">{home?.name}</span>
                      </div>
                      <div className="px-4 text-center">
                        <span className="font-heading text-xl font-black">
                          {match.home_score} - {match.away_score}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-1 justify-end">
                        <span className="text-sm font-medium truncate text-right">{away?.name}</span>
                        {away?.logo ? (
                          <img src={away.logo} alt="" className="w-8 h-8 rounded object-cover" />
                        ) : (
                          <div className="w-8 h-8 bg-secondary rounded flex items-center justify-center">
                            <Shield className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mini Standings */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold uppercase">
                Standings
              </h3>
              <Link
                to={`/public/${token}/standings`}
                className="text-xs text-primary hover:underline"
              >
                Full table
              </Link>
            </div>
            <div className="space-y-2">
              {standings.slice(0, 6).map((team, i) => (
                <div
                  key={team.team_id}
                  className={`flex items-center gap-3 p-2 rounded-lg ${
                    i < 4 ? "bg-primary/5 border-l-2 border-primary" : ""
                  }`}
                >
                  <span className="w-6 text-center font-bold text-muted-foreground">
                    {i + 1}
                  </span>
                  <span className="flex-1 truncate">{team.team_name}</span>
                  <span className="text-xs text-muted-foreground">{team.played}P</span>
                  <span className="font-bold text-primary w-8 text-right">{team.points}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* MVP Leaders */}
          {mvpLeaders.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-5 h-5 text-amber-500" />
                <h3 className="font-heading text-lg font-bold uppercase">
                  MVP Leaders
                </h3>
              </div>
              <div className="space-y-2">
                {mvpLeaders.map((player, i) => (
                  <div
                    key={player.player_id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50"
                  >
                    <span className="w-6 h-6 bg-amber-500/20 rounded flex items-center justify-center text-amber-500 font-bold text-sm">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">{player.player_name}</p>
                      <p className="text-xs text-muted-foreground">{player.team_name}</p>
                    </div>
                    <span className="font-heading text-xl font-black text-amber-500">
                      {player.mvp_awards}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Scorers */}
          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold uppercase">
                Top Scorers
              </h3>
              <Link
                to={`/public/${token}/player-stats`}
                className="text-xs text-primary hover:underline"
              >
                View all
              </Link>
            </div>
            {topScorers.length > 0 ? (
              <div className="space-y-2">
                {topScorers.map((player, i) => (
                  <div
                    key={player.player_id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-secondary/50"
                  >
                    <span className="w-6 h-6 bg-primary/20 rounded flex items-center justify-center text-primary font-bold text-sm">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm">{player.player_name}</p>
                      <p className="text-xs text-muted-foreground">{player.team_name}</p>
                    </div>
                    <span className="font-heading text-xl font-black text-primary">
                      {player.goals}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No goals yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
